import {
  useGetMany,
  useListContext,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Link as RouterLink } from "react-router";
import { Badge } from "@/components/ui/badge";

import { findDealLabel } from "../deals/dealUtils";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { ChampClass, Deal } from "../types";
import { isActiveEnrollment, splitEnrollments } from "./champFamilyUtils";

/**
 * A family's enrollments grouped into Active, In progress and Past, each row
 * showing the class and a stage badge. Reads the enrollments from the
 * surrounding ReferenceManyField.
 */
export const ChampFamilyEnrollments = () => {
  const translate = useTranslate();
  const { data: deals, error, isPending } = useListContext<Deal>();
  const classIds = [
    ...new Set(
      (deals ?? [])
        .map((deal) => deal.class_id)
        .filter((id): id is Identifier => id != null),
    ),
  ];
  const { data: classes = [] } = useGetMany<ChampClass>(
    "classes",
    { ids: classIds },
    { enabled: classIds.length > 0 },
  );

  if (isPending || error) return null;

  const classById = new Map(classes.map((c) => [c.id, c]));
  const { active, inProgress, past } = splitEnrollments(deals);
  // Active always shows; the other groups only when they have something.
  const groups = [
    { key: "active", deals: active },
    { key: "in_progress", deals: inProgress },
    { key: "past", deals: past },
  ].filter((group) => group.key === "active" || group.deals.length > 0);

  return (
    <div className="flex flex-col gap-6 pt-2">
      {groups.map(({ key, deals: groupDeals }) => (
        <section key={key}>
          <h6 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-1">
            {translate(`resources.companies.champ.enrollments_${key}`, {
              smart_count: groupDeals.length,
            })}
          </h6>
          {groupDeals.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              {translate("resources.companies.champ.no_enrollments")}
            </p>
          ) : (
            groupDeals.map((deal) => (
              <EnrollmentRow
                key={deal.id}
                deal={deal}
                champClass={
                  deal.class_id != null
                    ? classById.get(deal.class_id)
                    : undefined
                }
              />
            ))
          )}
        </section>
      ))}
    </div>
  );
};

const EnrollmentRow = ({
  deal,
  champClass,
}: {
  deal: Deal;
  champClass?: ChampClass;
}) => {
  const translate = useTranslate();
  const { dealStages } = useConfigurationContext();
  const stageLabel = deal.archived_at
    ? translate("resources.companies.champ.archived")
    : findDealLabel(dealStages, deal.stage);
  const variant = isActiveEnrollment(deal)
    ? "default"
    : deal.stage === "lost" || deal.archived_at
      ? "outline"
      : "secondary";

  return (
    <RouterLink
      to={`/deals/${deal.id}/show`}
      className="flex items-center justify-between gap-4 hover:bg-muted py-2 px-2 text-sm transition-colors no-underline"
    >
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">{deal.name}</div>
        <div className="text-muted-foreground truncate">
          {champClass
            ? champClass.program_name
              ? `${champClass.program_name} · ${champClass.name}`
              : champClass.name
            : translate("resources.companies.champ.no_class")}
        </div>
      </div>
      {isActiveEnrollment(deal) && (
        <Badge variant={deal.paid ? "secondary" : "destructive"}>
          {translate(
            deal.paid ? "resources.deals.paid" : "resources.deals.unpaid",
          )}
        </Badge>
      )}
      <Badge variant={variant}>{stageLabel}</Badge>
    </RouterLink>
  );
};
